import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { RefreshCwIcon } from 'lucide-react'
import {
  fetchRecurringJobSeries,
  formatRecurrenceDays,
  formatRecurrenceType,
  type RecurringJobSeries,
} from '../api/recurringJobs'
import { ListFilterSortBar } from '../components/ListFilterSortBar'
import {
  MobileEmptyState,
  MobileListRow,
  MobilePage,
  MobilePageHeader,
  mobile,
} from '../components/mobile'
import { useDivision } from '../contexts/DivisionContext'
import {
  EMPTY_DATE_RANGE,
  matchesDateRange,
  matchesMultiSelect,
  sortItems,
  uniqueFilterOptions,
  type DateRange,
  type SortDir,
} from '../lib/listFilterSort'
import { recordMatchesDivision } from '../lib/operationalDivisions'

const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'customer', label: 'Customer' },
  { value: 'next', label: 'Next run' },
  { value: 'frequency', label: 'Frequency' },
  { value: 'status', label: 'Active' },
]

function formatDate(value: string | null): string {
  if (!value) return '—'
  try {
    return new Date(value + 'T00:00:00').toLocaleDateString()
  } catch {
    return value
  }
}

function formatTime(value: string | null): string {
  if (!value) return ''
  return value.slice(0, 5)
}

export function RecurringJobs() {
  const navigate = useNavigate()
  const { currentDivision } = useDivision()
  const [rows, setRows] = useState<RecurringJobSeries[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [activeFilter, setActiveFilter] = useState('all')
  const [sourceFilter, setSourceFilter] = useState('all')
  const [customerFilter, setCustomerFilter] = useState<string[]>([])
  const [frequencyFilter, setFrequencyFilter] = useState<string[]>([])
  const [assignedFilter, setAssignedFilter] = useState<string[]>([])
  const [nextRunRange, setNextRunRange] = useState<DateRange>(EMPTY_DATE_RANGE)
  const [sortBy, setSortBy] = useState('name')
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        setLoading(true)
        const data = await fetchRecurringJobSeries()
        if (!cancelled) setRows(data)
      } catch (error) {
        console.error('Error loading recurring jobs:', error)
        if (!cancelled) setRows([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  const assignedName = (row: RecurringJobSeries) =>
    row.assigned_crew_name || row.assigned_employee_name || ''

  const customerOptions = useMemo(
    () => uniqueFilterOptions(rows.map((row) => row.customer_name)),
    [rows]
  )
  const frequencyOptions = useMemo(
    () => uniqueFilterOptions(rows.map((row) => row.recurrence_type), formatRecurrenceType),
    [rows]
  )
  const assignedOptions = useMemo(
    () => uniqueFilterOptions(rows.map(assignedName)),
    [rows]
  )

  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase()
    const matched = rows.filter((row) => {
      if (!recordMatchesDivision(row, currentDivision)) return false

      const matchesSearch =
        !q ||
        row.name.toLowerCase().includes(q) ||
        row.customer_name?.toLowerCase().includes(q) ||
        row.property_name?.toLowerCase().includes(q) ||
        row.contract_number?.toLowerCase().includes(q) ||
        row.job_number?.toLowerCase().includes(q)

      const matchesActive =
        activeFilter === 'all' ||
        (activeFilter === 'active' ? row.is_active : !row.is_active)
      const matchesSource = sourceFilter === 'all' || row.source === sourceFilter

      return (
        matchesSearch &&
        matchesActive &&
        matchesSource &&
        matchesMultiSelect(row.customer_name, customerFilter) &&
        matchesMultiSelect(row.recurrence_type, frequencyFilter) &&
        matchesMultiSelect(assignedName(row), assignedFilter) &&
        matchesDateRange(row.next_scheduled_date, nextRunRange)
      )
    })

    return sortItems(
      matched,
      (row) => {
        switch (sortBy) {
          case 'customer':
            return row.customer_name || ''
          case 'next':
            return row.next_scheduled_date || ''
          case 'frequency':
            return row.recurrence_type || ''
          case 'status':
            return row.is_active ? 0 : 1
          default:
            return row.name
        }
      },
      sortDir
    )
  }, [
    rows,
    searchTerm,
    activeFilter,
    sourceFilter,
    customerFilter,
    frequencyFilter,
    assignedFilter,
    nextRunRange,
    sortBy,
    sortDir,
    currentDivision,
  ])

  const openRow = (row: RecurringJobSeries) => {
    if (row.source === 'job' && row.job_id) {
      navigate(`/jobs/details/${row.job_id}`)
      return
    }
    if (row.contract_id) {
      navigate(`/contracts/details/${row.contract_id}`)
    }
  }

  return (
    <MobilePage>
      <MobilePageHeader
        title="Recurring Jobs"
        subtitle={
          <span className="hidden sm:inline">
            Recurrence series and visit groups — individual visits still appear on Jobs
          </span>
        }
        actions={
          <button type="button" onClick={() => navigate('/jobs')} className={mobile.secondaryBtn}>
            <RefreshCwIcon className="w-4 h-4" />
            <span className="hidden sm:inline">All Jobs</span>
            <span className="sm:hidden">Jobs</span>
          </button>
        }
      />

      <ListFilterSortBar
        search={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search recurring series..."
        className="lg:p-6"
        filters={[
          {
            label: 'Status',
            value: activeFilter,
            onChange: setActiveFilter,
            ariaLabel: 'Active filter',
            options: [
              { value: 'all', label: 'All statuses' },
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Paused / inactive' },
            ],
          },
          {
            label: 'Source',
            value: sourceFilter,
            onChange: setSourceFilter,
            ariaLabel: 'Source filter',
            options: [
              { value: 'all', label: 'All sources' },
              { value: 'visit_group', label: 'Contract visit groups' },
              { value: 'job', label: 'Job series' },
            ],
          },
          {
            type: 'multi',
            label: 'Customer',
            value: customerFilter,
            onChange: setCustomerFilter,
            options: customerOptions,
          },
          {
            type: 'multi',
            label: 'Frequency',
            value: frequencyFilter,
            onChange: setFrequencyFilter,
            options: frequencyOptions,
          },
          {
            type: 'multi',
            label: 'Assigned to',
            value: assignedFilter,
            onChange: setAssignedFilter,
            options: assignedOptions,
          },
          {
            type: 'daterange',
            label: 'Next run',
            value: nextRunRange,
            onChange: setNextRunRange,
          },
        ]}
        sortValue={sortBy}
        onSortChange={setSortBy}
        sortOptions={SORT_OPTIONS}
        sortDir={sortDir}
        onSortDirChange={setSortDir}
      />

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : (
        <>
          <div className="native-mobile-list space-y-2">
            {filtered.length === 0 ? (
              <MobileEmptyState
                icon={<RefreshCwIcon className="w-12 h-12" />}
                title="No recurring series found"
                description="Try adjusting search or filters. Individual job visits remain on the Jobs list."
              />
            ) : (
              filtered.map((row) => (
                <MobileListRow
                  key={`${row.source}-${row.id}`}
                  onClick={() => openRow(row)}
                  title={row.name}
                  subtitle={
                    row.source === 'visit_group'
                      ? row.contract_number
                        ? `Contract #${row.contract_number}`
                        : 'Visit group'
                      : row.job_number
                        ? `Job #${row.job_number}`
                        : 'Job series'
                  }
                  badges={
                    <>
                      <span
                        className={`${mobile.badge} ${
                          row.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {row.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <span className={`${mobile.badge} bg-blue-100 text-blue-800`}>
                        {formatRecurrenceType(row.recurrence_type)}
                      </span>
                    </>
                  }
                  meta={
                    <>
                      <div className="truncate">{row.customer_name || '—'}</div>
                      <div className="truncate text-gray-500">
                        Next {formatDate(row.next_scheduled_date)}
                      </div>
                    </>
                  }
                />
              ))
            )}
          </div>

          <div className="native-desktop-table bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Series
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Customer / Property
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Frequency
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Days
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Window
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Next run
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Occurrences
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Assigned
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-10 text-center text-sm text-gray-500">
                        No recurring series match your filters.
                      </td>
                    </tr>
                  ) : (
                    filtered.map((row) => (
                      <tr
                        key={`${row.source}-${row.id}`}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => openRow(row)}
                      >
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">{row.name}</div>
                          <div className="text-xs text-gray-500">
                            {row.source === 'visit_group'
                              ? row.contract_number
                                ? `Contract #${row.contract_number}`
                                : 'Visit group'
                              : row.job_number
                                ? `Job #${row.job_number}`
                                : 'Job series'}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <div>{row.customer_name || '—'}</div>
                          <div className="text-xs text-gray-500 truncate max-w-[14rem]">
                            {row.property_name || row.property_address || '—'}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {formatRecurrenceType(row.recurrence_type)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {formatRecurrenceDays(row.recurrence_days)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <div>
                            {formatDate(row.start_date)}
                            {row.end_date ? ` → ${formatDate(row.end_date)}` : ''}
                          </div>
                          {(row.scheduled_start_time || row.scheduled_end_time) && (
                            <div className="text-xs text-gray-500">
                              {[formatTime(row.scheduled_start_time), formatTime(row.scheduled_end_time)]
                                .filter(Boolean)
                                .join(' – ')}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {formatDate(row.next_scheduled_date)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{row.occurrence_count}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {row.assigned_crew_name ||
                            row.assigned_employee_name ||
                            '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                              row.is_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {row.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </MobilePage>
  )
}
